import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiEndpoints } from "@/lib/api";
import { api } from "@/lib/api-client";
import type { ChangeOrder } from "@/types/changeOrder";

const getChangeOrders = async (projectId?: string): Promise<Partial<ChangeOrder>[]> => {
  const res: any = await api.get(apiEndpoints.changeOrders.getAll(projectId));
  return res.data;
};

export const useGetChangeOrders = (projectId?: string) => {
  return useQuery<Partial<ChangeOrder>[]>({
    queryKey: ["change-orders", projectId],
    queryFn: () => getChangeOrders(projectId),
  });
};

const getChangeOrderById = async (id: string, projectId?: string): Promise<Partial<ChangeOrder>> => {
  const res: any = await api.get(apiEndpoints.changeOrders.getOneById(id, projectId));
  return res.data;
};

export const useGetChangeOrderById = (id: string, projectId?: string) => {
  return useQuery<Partial<ChangeOrder>>({
    queryKey: ["change-orders", id, projectId],
    queryFn: () => getChangeOrderById(id, projectId),
    enabled: !!id,
  });
};

export const useCreateChangeOrder = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ChangeOrder>) => {
      return api.post(apiEndpoints.changeOrders.create(projectId), data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["change-orders"] });
    },
  });
};

export const useUpdateChangeOrder = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ChangeOrder> }) => {
      return api.patch(apiEndpoints.changeOrders.update(id, projectId), data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["change-orders"] });
      queryClient.invalidateQueries({ queryKey: ["change-orders", variables.id] });
    },
  });
};

export const useDeleteChangeOrder = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      return api.delete(apiEndpoints.changeOrders.delete(id, projectId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["change-orders"] });
    },
  });
};

const getUnbilledCount = async (projectId?: string): Promise<{ count: number }> => {
  const res: any = await api.get(apiEndpoints.changeOrders.unbilledCount(projectId));
  return res.data;
};

export const useUnbilledCount = (projectId?: string) => {
  return useQuery<{ count: number }>({
    queryKey: ["change-orders", "unbilled-count", projectId],
    queryFn: () => getUnbilledCount(projectId),
    enabled: !!projectId,
  });
};

const getChangeOrderPdfUrl = async (id: string): Promise<{ url: string }> => {
  const res: any = await api.get(apiEndpoints.changeOrders.getPdfUrl(id));
  return res.data;
};

export const useGetChangeOrderPdfUrl = (id: string) => {
  return useQuery<{ url: string }>({
    queryKey: ["change-orders", id, "pdf"],
    queryFn: () => getChangeOrderPdfUrl(id),
    enabled: !!id,
  });
};
